export type MealLogEventType = 'prepared' | 'consumed' | 'skipped'
export type MealLogSourceType = 'protocol' | 'custom'
export type MealAdherence = 'on_plan' | 'substituted' | 'skipped' | 'unlogged'

export type MacroTotals = {
  calories_kcal: number
  protein_g: number
  carbs_g: number
  fat_g: number
}

export type MealLogRow = {
  id: string
  log_date: string
  day_id: string | null
  meal_block_name: string
  meal_protocol_id: string | null
  event_type: MealLogEventType
  source_type: MealLogSourceType
  dish_name: string
  calories_kcal: number
  protein_g: number
  carbs_g: number
  fat_g: number
  portion_pct: number
  notes: string | null
  logged_at: string
  grocery_depleted?: boolean
}

export type MealSlotStatus = {
  meal_block_name: string
  prepared: boolean
  intake: MealAdherence
  consumed_entry: MealLogRow | null
  prepared_entries: MealLogRow[]
}

export function scaleMacros(macros: MacroTotals, portionPct: number): MacroTotals {
  const scale = portionPct / 100
  return {
    calories_kcal: Math.round(macros.calories_kcal * scale),
    protein_g: Math.round(macros.protein_g * scale),
    carbs_g: Math.round(macros.carbs_g * scale),
    fat_g: Math.round(macros.fat_g * scale),
  }
}

export function sumMacroTotals(rows: Pick<MealLogRow, 'calories_kcal' | 'protein_g' | 'carbs_g' | 'fat_g'>[]): MacroTotals {
  return rows.reduce(
    (acc, row) => ({
      calories_kcal: acc.calories_kcal + (row.calories_kcal ?? 0),
      protein_g: acc.protein_g + (row.protein_g ?? 0),
      carbs_g: acc.carbs_g + (row.carbs_g ?? 0),
      fat_g: acc.fat_g + (row.fat_g ?? 0),
    }),
    { calories_kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 },
  )
}

export function resolveIntakeAdherence(entry: MealLogRow | null): MealAdherence {
  if (!entry) return 'unlogged'
  if (entry.event_type === 'skipped') return 'skipped'
  if (entry.source_type === 'custom') return 'substituted'
  return 'on_plan'
}

export function buildSlotStatuses(logs: MealLogRow[], mealBlocks: string[]): MealSlotStatus[] {
  const byBlock = new Map<string, MealLogRow[]>()
  for (const row of logs) {
    const list = byBlock.get(row.meal_block_name) ?? []
    list.push(row)
    byBlock.set(row.meal_block_name, list)
  }

  return mealBlocks.map((block) => {
    const rows = byBlock.get(block) ?? []
    const preparedEntries = rows.filter((r) => r.event_type === 'prepared')
    const consumed = rows.find((r) => r.event_type === 'consumed' || r.event_type === 'skipped') ?? null
    return {
      meal_block_name: block,
      prepared: preparedEntries.length > 0,
      intake: resolveIntakeAdherence(consumed),
      consumed_entry: consumed,
      prepared_entries: preparedEntries,
    }
  })
}

export type DailyNutritionSummary = {
  log_date: string
  planned: MacroTotals
  actual: MacroTotals
  slots_logged: number
  slots_on_plan: number
  slots_substituted: number
  slots_skipped: number
  slots_unlogged: number
  adherence_pct: number | null
}

export function summarizeDailyNutrition(
  logDate: string,
  plannedMeals: MacroTotals[],
  intakeLogs: MealLogRow[],
  slotCount: number,
): DailyNutritionSummary {
  const consumedOrSkipped = intakeLogs.filter((r) => r.event_type === 'consumed' || r.event_type === 'skipped')
  const actualConsumed = consumedOrSkipped.filter((r) => r.event_type === 'consumed')
  const planned = sumMacroTotals(plannedMeals)
  const actual = sumMacroTotals(actualConsumed)

  const slotsOnPlan = consumedOrSkipped.filter((r) => resolveIntakeAdherence(r) === 'on_plan').length
  const slotsSubstituted = consumedOrSkipped.filter((r) => resolveIntakeAdherence(r) === 'substituted').length
  const slotsSkipped = consumedOrSkipped.filter((r) => resolveIntakeAdherence(r) === 'skipped').length
  const slotsLogged = consumedOrSkipped.length
  const slotsUnlogged = Math.max(0, slotCount - slotsLogged)

  const adherencePct =
    slotCount > 0 ? Math.round(((slotsOnPlan + slotsSubstituted * 0.5) / slotCount) * 100) : null

  return {
    log_date: logDate,
    planned,
    actual,
    slots_logged: slotsLogged,
    slots_on_plan: slotsOnPlan,
    slots_substituted: slotsSubstituted,
    slots_skipped: slotsSkipped,
    slots_unlogged: slotsUnlogged,
    adherence_pct: adherencePct,
  }
}

export function buildNutritionCoachNote(summary: DailyNutritionSummary): string | null {
  if (summary.slots_logged === 0) {
    return 'No meals logged yesterday — Kilo is still guessing your fuel intake.'
  }

  const parts: string[] = []
  if (summary.slots_substituted > 0) {
    parts.push(`${summary.slots_substituted} meal${summary.slots_substituted === 1 ? '' : 's'} off-plan`)
  }
  if (summary.slots_skipped > 0) {
    parts.push(`${summary.slots_skipped} skipped`)
  }
  if (summary.slots_unlogged > 0) {
    const mealWord = summary.slots_unlogged === 1 ? 'meal' : 'meals'
    parts.push(`${summary.slots_unlogged} planned ${mealWord} not logged`)
  }

  const proteinGap = summary.planned.protein_g - summary.actual.protein_g
  if (proteinGap >= 25) {
    parts.push(`${proteinGap}g protein short vs plan`)
  }

  if (parts.length === 0) {
    return summary.adherence_pct != null && summary.adherence_pct >= 80
      ? 'Yesterday matched your nutrition protocol closely.'
      : null
  }

  return `Yesterday: ${parts.join(' · ')}.`
}
